import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../../clients/supabaseClient";
import { useToast } from "../../hooks/useToast";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "@heroicons/react/24/outline";

type Category = {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  created_at: string;
  icon_url?: string | null;
  on_home_page: boolean;
};

type CategoryDepartment = {
  number: number;
  name: string | null;
};

type DepartmentFormRow = {
  number: string;
  name: string;
};

type CategoryWithDepartments = Category & {
  departments: CategoryDepartment[];
};


const ITEMS_PER_PAGE = 20;

export default function CategoriesPage() {
  const { success, error: showError } = useToast();
  const [items, setItems] = useState<CategoryWithDepartments[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [onHomePage, setOnHomePage] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // modal + form state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [departments, setDepartments] = useState<DepartmentFormRow[]>([]);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [editing, setEditing] = useState<CategoryWithDepartments | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const from = (page - 1) * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE - 1;

    let query = supabase
        .from("categories")
        .select("id,name,description,created_at,icon_url,on_home_page", { count: "exact" });

    if (q) {
      query = query.ilike("name", `%${q}%`);
    }

    const { data, error, count } = await query
        .order("name", { ascending: true })
        .range(from, to);

    if (error) {
      console.error("Supabase Error:", error);
      setLoading(false);
      return;
    }

    const categories = (data ?? []) as Category[];

    const { data: mapRows, error: mapError } = await supabase
        .from("category_department_map")
        .select("category_id, department_number, department_name");

    if (mapError) {
      console.error("Supabase Error:", mapError);
      setLoading(false);
      return;
    }

    const deptMap = new Map<string, CategoryDepartment[]>();

    for (const row of mapRows ?? []) {
      const categoryId = String(row.category_id);
      const deptNumber = Number(row.department_number);
      const deptName = row.department_name ? String(row.department_name) : null;

      if (!Number.isFinite(deptNumber)) continue;

      const existing = deptMap.get(categoryId) ?? [];
      const alreadyExists = existing.some((d) => d.number === deptNumber);

      if (!alreadyExists) {
        existing.push({ number: deptNumber, name: deptName });
        deptMap.set(categoryId, existing);
      }
    }

    setItems(
        categories.map((category) => ({
          ...category,
          departments: (deptMap.get(category.id) ?? []).sort((a, b) => a.number - b.number),
        }))
    );
    setTotalCount(count ?? 0);
    setLoading(false);
  }, [page, q]);


  const fetchExistingImages = async () => {
    const { data, error } = await supabase.storage
        .from("category_images")
        .list("", { limit: 100, offset: 0 });

    if (error) return;

    if (data) {
      const publicUrls = data.map((file) => {
        const { data: publicUrl } = supabase.storage
            .from("category_images")
            .getPublicUrl(file.name);
        return publicUrl.publicUrl;
      });
      setExistingImages(publicUrls);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchExistingImages();
  }, [fetchItems]);

  // Reset page when searching
  useEffect(() => {
    setPage(1);
  }, [q]);

  const resetForm = () => {
    setName("");
    setDesc("");
    setDepartments([]);
    setIconFile(null);
    setIconUrl(null);
    setEditing(null);
    setOnHomePage(false);
  };

  const openModal = (c?: CategoryWithDepartments) => {
    if (c) {
      setEditing(c);
      setName(c.name);
      setDesc(c.description ?? "");
      setDepartments(
          c.departments.map((department) => ({
            number: String(department.number),
            name: department.name ?? "",
          }))
      );
      setIconUrl(c.icon_url ?? null);
      setOnHomePage(c.on_home_page ?? false);
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };


  const addDepartmentRow = () => {
    setDepartments((prev) => [{ number: "", name: "" }, ...prev]);
  };


  const updateDepartmentRow = (
      index: number,
      field: keyof DepartmentFormRow,
      value: string
  ) => {
    setDepartments((prev) =>
        prev.map((department, departmentIndex) =>
            departmentIndex === index
                ? { ...department, [field]: value }
                : department
        )
    );
  };

  const removeDepartmentRow = (index: number) => {
    setDepartments((prev) =>
        prev.filter((_, departmentIndex) => departmentIndex !== index)
    );
  };


  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    let finalIconUrl = iconUrl;
    if (iconFile) {
      const safeName = iconFile.name.replace(/\s+/g, "-").toLowerCase();
      const filePath = `${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
          .from("category_images")
          .upload(filePath, iconFile, {
            upsert: true,
            contentType: iconFile.type,
          });

      if (uploadError) {
        console.error("UPLOAD ERROR:", uploadError);
        showError(`Image upload failed: ${uploadError.message}`);
        setSaving(false);
        return;
      }

      const { data: publicUrl } = supabase.storage
          .from("category_images")
          .getPublicUrl(filePath);

      finalIconUrl = publicUrl.publicUrl;
    }

    const parsedDepartments = departments
        .map((department) => ({
          number: Number(department.number.trim()),
          name: department.name.trim() || null,
        }))
        .filter((department) => Number.isFinite(department.number));

    const uniqueDepartmentNumbers = new Set<number>();

    for (const department of parsedDepartments) {
      if (uniqueDepartmentNumbers.has(department.number)) {
        showError(`Department number ${department.number} is duplicated.`);
        setSaving(false);
        return;
      }

      uniqueDepartmentNumbers.add(department.number);
    }

    const payload = {
      name: name.trim(),
      description: desc || null,
      icon_url: finalIconUrl,
      on_home_page: onHomePage,
    };

    const res = editing
        ? await supabase.from("categories").update(payload).eq("id", editing.id)
        : await supabase.from("categories").insert(payload).select("id").single();

    if (res.error) {
      console.error("CATEGORY SAVE ERROR:", res.error);
      showError(`Could not save category: ${res.error.message}`);
      setSaving(false);
      return;
    }

    const categoryId = editing?.id ?? res.data?.id;
    if (!categoryId) {
      showError("Could not save category: no category id was returned.");
      setSaving(false);
      return;
    }

    const { data: existingMapRows, error: existingMapError } = await supabase
        .from("category_department_map")
        .select("department_number")
        .eq("category_id", categoryId);

    if (existingMapError) {
      console.error("CATEGORY MAP LOAD ERROR:", existingMapError);
      showError(`Could not load existing departments: ${existingMapError.message}`);
      setSaving(false);
      return;
    }

    const existingDepartmentNumbers = new Set(
        (existingMapRows ?? []).map((row) => Number(row.department_number))
    );
    const nextDepartmentNumbers = new Set(
        parsedDepartments.map((department) => department.number)
    );

    const departmentsToRemove = [...existingDepartmentNumbers].filter(
        (departmentNumber) => !nextDepartmentNumbers.has(departmentNumber)
    );

    if (departmentsToRemove.length > 0) {
      const { data: blockingProducts, error: blockingProductsError } = await supabase
          .from("products")
          .select("id, name, department_number")
          .in("department_number", departmentsToRemove)
          .limit(1);

      if (blockingProductsError) {
        console.error("PRODUCT CHECK ERROR:", blockingProductsError);
        showError(`Could not check products for removed departments: ${blockingProductsError.message}`);
        setSaving(false);
        return;
      }

      if ((blockingProducts ?? []).length > 0) {
        showError(
            "One or more removed department numbers are still used by products, so they cannot be unlinked yet."
        );
        setSaving(false);
        return;
      }

      const { error: deleteMapError } = await supabase
          .from("category_department_map")
          .delete()
          .eq("category_id", categoryId)
          .in("department_number", departmentsToRemove);

      if (deleteMapError) {
        console.error("CATEGORY MAP DELETE ERROR:", deleteMapError);
        showError(`Could not remove departments: ${deleteMapError.message}`);
        setSaving(false);
        return;
      }
    }

    if (parsedDepartments.length > 0) {
      const { error: upsertMapError } = await supabase
          .from("category_department_map")
          .upsert(
              parsedDepartments.map((department) => ({
                category_id: categoryId,
                department_number: department.number,
                department_name: department.name,
              })),
              {
                onConflict: "department_number",
              }
          );

      if (upsertMapError) {
        console.error("CATEGORY MAP UPSERT ERROR:", upsertMapError);
        showError(`Could not save departments: ${upsertMapError.message}`);
        setSaving(false);
        return;
      }
    }

    const savedName = payload.name;
    const departmentCount = parsedDepartments.length;

    setSaving(false);
    setIsModalOpen(false);
    resetForm();

    success(
        departmentCount > 0
            ? `"${savedName}" saved with ${departmentCount} department${departmentCount === 1 ? "" : "s"}.`
            : `"${savedName}" saved.`
    );
    await fetchItems();
    await fetchExistingImages();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this category?")) return;

    const { error: mapDeleteError } = await supabase
        .from("category_department_map")
        .delete()
        .eq("category_id", id);

    if (mapDeleteError) {
      console.error("CATEGORY MAP DELETE ERROR:", mapDeleteError);
      return;
    }

    const { error } = await supabase.from("categories").delete().eq("id", id);
    if (!error) fetchItems();
  };

  const displayItems = items;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-stone-900">Categories</h2>
              <p className="text-sm text-stone-500 mt-1">Manage and track all category inventory</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search Bar */}
              <div >
                <div className="flex-initial w-full sm:w-96 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search categories..."
                      className="w-full border border-stone-300 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white shadow-sm"
                  />
                </div>
              </div>
              <button
                  onClick={() => openModal()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#e6186c] text-white text-sm font-medium hover:bg-[#d01560] transition-colors shadow-sm"
              >
                <PlusIcon className="h-4 w-4" />
                Add Category
              </button>
            </div>
          </div>



        </div>

        {/* List Container */}
        <div className="rounded-2xl   overflow-hidden  ">


          {loading ? (
              <div className="py-20 text-center text-stone-400 text-sm">Loading categories…</div>
          ) : displayItems.length === 0 ? (
              <div className="py-20 text-center text-stone-400 text-sm">No categories found</div>
          ) : (
              <div className="space-y-3">
                {displayItems.map((c) => (
                    <div
                        key={c.id}
                        className="rounded-2xl border border-stone-200 bg-white shadow-sm px-4 py-4 flex items-center justify-between hover:bg-stone-50/50 transition-colors"
                    >
                      <div className="flex items-start gap-4 flex-1">
                        {c.icon_url ? (
                            <img src={c.icon_url} alt="" className="w-12 h-12 object-cover rounded-lg border border-stone-200" />
                        ) : (
                            <div className="w-12 h-12 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center">
                              <span className="text-xs text-stone-400">—</span>
                            </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-stone-900 truncate">{c.name}</p>

                            {c.on_home_page && (
                                <span className="inline-flex items-center rounded-full bg-pink-50 px-2 py-0.5 text-[10px] font-medium text-[#e6186c] ring-1 ring-inset ring-pink-200">
                        Home Page
                      </span>
                            )}
                          </div>

                          {c.departments.length > 0 && (() => {
                            const isExpanded = expandedCategories[c.id];
                            const visibleDepartments = isExpanded ? c.departments : c.departments.slice(0, 3);
                            const hiddenCount = c.departments.length - visibleDepartments.length;

                            return (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {visibleDepartments.map((dept) => (
                                      <span
                                          key={`${dept.number}-${dept.name ?? "unknown"}`}
                                          className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-[11px] text-stone-700"
                                      >
                            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#e6186c] px-1 text-[10px] font-bold leading-none text-white">
                              {dept.number}
                            </span>
                            <span className="max-w-[140px] truncate font-medium">
                              {dept.name ?? "Unnamed Department"}
                            </span>
                          </span>
                                  ))}

                                  {hiddenCount > 0 && !isExpanded && (
                                      <button
                                          type="button"
                                          onClick={() =>
                                              setExpandedCategories((prev) => ({
                                                ...prev,
                                                [c.id]: true,
                                              }))
                                          }
                                          className="inline-flex items-center rounded-full border border-stone-200 bg-white px-2.5 py-1 text-[11px] font-medium text-stone-500 hover:bg-stone-50 hover:text-stone-700"
                                      >
                                        +{hiddenCount} more
                                      </button>
                                  )}

                                  {isExpanded && c.departments.length > 3 && (
                                      <button
                                          type="button"
                                          onClick={() =>
                                              setExpandedCategories((prev) => ({
                                                ...prev,
                                                [c.id]: false,
                                              }))
                                          }
                                          className="inline-flex items-center rounded-full border border-stone-200 bg-white px-2.5 py-1 text-[11px] font-medium text-stone-500 hover:bg-stone-50 hover:text-stone-700"
                                      >
                                        Show less
                                      </button>
                                  )}
                                </div>
                            );
                          })()}

                          {c.description && (
                              <p className="text-xs text-stone-500 line-clamp-1 mt-2">{c.description}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                            onClick={() => openModal(c)}
                            className="p-2 rounded-lg text-stone-600 hover:bg-stone-100 transition-colors"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => remove(c.id)}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                ))}
              </div>
          )}

          {/* Pagination Footer */}
          {!loading && totalCount > 0 && (
              <div className="px-4 py-4 border-t border-stone-200 bg-stone-50/30 flex items-center justify-between">
                <div className="text-xs text-stone-500">
                  Showing <span className="font-medium text-stone-900">{(page - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="font-medium text-stone-900">{Math.min(page * ITEMS_PER_PAGE, totalCount)}</span> of <span className="font-medium text-stone-900">{totalCount}</span> categories
                </div>
                <div className="flex items-center gap-2">
                  <button
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                      className="p-2 rounded-lg border border-stone-300 bg-white hover:bg-stone-50 disabled:opacity-40 transition-colors shadow-sm"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-xs font-medium text-stone-600 px-2">Page {page} of {totalPages}</span>
                  <button
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}
                      className="p-2 rounded-lg border border-stone-300 bg-white hover:bg-stone-50 disabled:opacity-40 transition-colors shadow-sm"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
          )}
        </div>



        {/* Modal */}
        {isModalOpen && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
              <div className="bg-white w-full max-w-2xl rounded-2xl shadow-sm max-h-[90vh] overflow-y-auto">
                <div className="h-1 bg-[#e6186c] w-full" />

                <div className="p-6">
                  <h3 className="text-lg font-semibold text-stone-900 mb-6">
                    {editing ? "Edit Category" : "Add Category"}
                  </h3>

                  <form onSubmit={submit} className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">Name</label>
                      <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-[#e6186c] transition-ring bg-white"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">Description</label>
                      <textarea
                          value={desc}
                          onChange={(e) => setDesc(e.target.value)}
                          rows={3}
                          className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-[#e6186c] transition-ring bg-white"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div>
                          <label className="block text-sm font-semibold text-stone-700">
                            Departments
                          </label>
                          <p className="text-xs text-stone-500 mt-0.5">
                            Add department numbers and names connected to this category.
                          </p>
                        </div>

                        <button
                            type="button"
                            onClick={addDepartmentRow}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50"
                        >
                          <PlusIcon className="h-4 w-4" />
                          Add Department
                        </button>
                      </div>

                      {departments.length === 0 ? (
                          <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50/60 px-4 py-5 text-center">
                            <p className="text-sm font-medium text-stone-700">
                              No departments added yet
                            </p>
                            <p className="text-xs text-stone-500 mt-1">
                              Click “Add Department” to create one for this category.
                            </p>
                          </div>
                      ) : (
                          <div className="space-y-2">
                            {departments.map((department, index) => (
                                <div
                                    key={index}
                                    className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-2 rounded-lg border border-stone-200 bg-stone-50/50 p-3"
                                >
                                  <div>
                                    <label className="block text-xs font-medium text-stone-600 mb-1">
                                      Number
                                    </label>
                                    <input
                                        type="number"
                                        value={department.number}
                                        onChange={(e) =>
                                            updateDepartmentRow(index, "number", e.target.value)
                                        }
                                        placeholder="101"
                                        className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-[#e6186c] bg-white"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-stone-600 mb-1">
                                      Name
                                    </label>
                                    <input
                                        type="text"
                                        value={department.name}
                                        onChange={(e) =>
                                            updateDepartmentRow(index, "name", e.target.value)
                                        }
                                        placeholder="Produce, Bakery, Deli..."
                                        className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-[#e6186c] bg-white"
                                    />
                                  </div>

                                  <div className="flex sm:items-end">
                                    <button
                                        type="button"
                                        onClick={() => removeDepartmentRow(index)}
                                        className="inline-flex h-10 w-full sm:w-10 items-center justify-center rounded-lg text-red-600 hover:bg-red-50"
                                        title="Remove department"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                            ))}
                          </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 bg-stone-50/50">
                      <input
                          type="checkbox"
                          id="on_home_page"
                          checked={onHomePage}
                          onChange={(e) => setOnHomePage(e.target.checked)}
                          className="h-4 w-4 rounded border-stone-300 text-[#e6186c] focus:ring-[#e6186c] accent-[#e6186c]"
                      />
                      <label htmlFor="on_home_page" className="text-sm font-medium text-stone-700 cursor-pointer">
                        Display on Home Page
                      </label>
                    </div>


                    {/* Icon */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">Icon</label>

                      {iconUrl && (
                          <div className="mb-3 relative inline-block">
                            <img
                                src={iconUrl}
                                alt="Preview"
                                className="w-16 h-16 object-cover rounded-lg border border-stone-200"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                  setIconUrl(null);
                                  setIconFile(null);
                                }}
                                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow"
                                title="Remove icon"
                            >
                              ✕
                            </button>
                          </div>
                      )}

                      <input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                              setIconFile(e.target.files ? e.target.files[0] : null)
                          }
                          className="mb-3 block text-sm text-stone-600 file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border file:border-stone-300 file:text-sm file:font-medium file:bg-white file:text-stone-700 hover:file:bg-stone-50"
                      />

                      {existingImages.length > 0 && (
                          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 rounded-lg border border-stone-200 bg-stone-50">
                            {existingImages.map((url, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                      setIconUrl(url);
                                      setIconFile(null);
                                    }}
                                    className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                        iconUrl === url
                                            ? "border-[#e6186c] ring-2 ring-[#e6186c]/20"
                                            : "border-stone-200 hover:border-stone-300"
                                    }`}
                                >
                                  <img
                                      src={url}
                                      alt="option"
                                      className="w-full h-full object-cover"
                                  />
                                </button>
                            ))}
                          </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 mt-6 border-t border-stone-100">
                      <button
                          type="button"
                          onClick={() => setIsModalOpen(false)}
                          className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2.5 rounded-lg bg-[#e6186c] text-white text-sm font-medium hover:bg-[#d01560] transition-colors disabled:opacity-60"
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
        )}
      </div>
  );
}
